// A label + numeric input + draggable bar. History-friendly via begin/end.

import { el, on, formatNum } from '@/lib/dom';

export type SliderOpts = {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  format?: (v: number) => string;
  onInput: (v: number) => void;
  onBegin?: () => void;
  onEnd?: () => void;
};

export function slider(opts: SliderOpts): HTMLElement {
  const step = opts.step ?? (opts.max - opts.min < 5 ? 0.01 : 1);
  const fmt = opts.format ?? ((v: number) => formatNum(v, 2));
  let value = clamp(opts.value, opts.min, opts.max);

  const valueEl = el('span', { class: 'sl-val' }, [fmt(value)]);
  const bar = el('div', { class: 'sl-bar' });
  const fill = el('div', { class: 'sl-fill' });
  bar.appendChild(fill);

  const root = el(
    'div',
    {
      class: 'sl',
      role: 'slider',
      tabindex: 0,
      ariaLabel: opts.label,
    },
    [
      el('div', { class: 'sl-row' }, [
        el('span', { class: 'sl-label' }, [opts.label]),
        valueEl,
      ]),
      bar,
    ],
  );
  root.setAttribute('aria-valuemin', String(opts.min));
  root.setAttribute('aria-valuemax', String(opts.max));
  root.setAttribute('aria-valuenow', String(value));

  function setVisualOnly(v: number): void {
    value = clamp(v, opts.min, opts.max);
    if (step > 0) value = Math.round(value / step) * step;
    valueEl.textContent = fmt(value);
    const t = (value - opts.min) / (opts.max - opts.min || 1);
    fill.style.width = `${t * 100}%`;
    root.setAttribute('aria-valuenow', String(value));
  }

  function update(v: number): void {
    setVisualOnly(v);
    opts.onInput(value);
  }

  setVisualOnly(value);

  // Pointer drag
  let dragging = false;
  on(bar, 'pointerdown', (ev) => {
    bar.setPointerCapture(ev.pointerId);
    dragging = true;
    opts.onBegin?.();
    applyFromPointer(ev);
  });
  on(bar, 'pointermove', (ev) => {
    if (!dragging) return;
    applyFromPointer(ev);
  });
  on(bar, 'pointerup', (ev) => {
    if (!dragging) return;
    dragging = false;
    bar.releasePointerCapture(ev.pointerId);
    opts.onEnd?.();
  });
  on(bar, 'pointercancel', () => {
    if (!dragging) return;
    dragging = false;
    opts.onEnd?.();
  });

  function applyFromPointer(ev: PointerEvent): void {
    const rect = bar.getBoundingClientRect();
    const t = (ev.clientX - rect.left) / rect.width;
    update(opts.min + t * (opts.max - opts.min));
  }

  // Keyboard: arrow keys
  on(root, 'keydown', (ev) => {
    const key = ev.key;
    let delta = 0;
    if (key === 'ArrowLeft' || key === 'ArrowDown') delta = -step;
    else if (key === 'ArrowRight' || key === 'ArrowUp') delta = step;
    else if (key === 'Home') {
      ev.preventDefault();
      opts.onBegin?.();
      update(opts.min);
      opts.onEnd?.();
      return;
    } else if (key === 'End') {
      ev.preventDefault();
      opts.onBegin?.();
      update(opts.max);
      opts.onEnd?.();
      return;
    } else return;
    if (ev.shiftKey) delta *= 10;
    ev.preventDefault();
    opts.onBegin?.();
    update(value + delta);
    opts.onEnd?.();
  });

  // Public API to refresh externally (e.g. on undo)
  (root as unknown as { refresh: (v: number) => void }).refresh = (v: number) =>
    setVisualOnly(v);

  return root;
}

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}
